import React, { useEffect, useState, useRef } from 'react';
import { alpha, Box } from '@mui/material';

// [FIX #2] SoundMeter now accepts an optional external stream prop.
// When provided, it uses the existing recording stream's audio tracks
// instead of opening a competing getUserMedia stream that can steal
// the microphone from the recorder and cause missing audio.
interface ISoundMeterProps {
  stream?: MediaStream | null;
}

export const SoundMeter: React.FC<ISoundMeterProps> = ({
  stream: externalStream,
}) => {
  const bars = 7;
  const [levels, setLevels] = useState<number[]>(new Array(bars).fill(0));
  const animationRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const ownStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isUsingOwnStreamRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const initAudioAnalyser = (stream: MediaStream) => {
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0 || audioTracks[0].readyState === 'ended') {
        console.warn('SoundMeter: stream has no active audio tracks');
        return;
      }

      try {
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 32;
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        analyserRef.current = analyser;
        dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

        const updateLevels = () => {
          if (!isMounted || !analyserRef.current || !dataArrayRef.current)
            return;

          analyserRef.current.getByteFrequencyData(dataArrayRef.current);
          const randomIndex = Math.floor(
            Math.random() * dataArrayRef.current.length,
          );
          const newLevel = dataArrayRef.current[randomIndex];

          setLevels((prevLevels) => [...prevLevels.slice(1), newLevel]);
          setTimeout(updateLevels, 75);
        };

        updateLevels();
      } catch (err) {
        console.warn('SoundMeter: Failed to initialize audio analyser:', err);
      }
    };

    if (externalStream) {
      // Use the shared recording stream — do NOT open our own
      isUsingOwnStreamRef.current = false;
      initAudioAnalyser(externalStream);
    } else {
      // Fallback: only if no external stream is provided (shouldn't happen in practice)
      isUsingOwnStreamRef.current = true;
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          ownStreamRef.current = stream;
          initAudioAnalyser(stream);
        })
        .catch((err) => {
          console.warn('SoundMeter: Failed to get audio stream:', err);
        });
    }

    return () => {
      isMounted = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Only stop tracks if WE created the stream (not the shared recording stream)
      if (isUsingOwnStreamRef.current && ownStreamRef.current) {
        ownStreamRef.current.getTracks().forEach((track) => track.stop());
        ownStreamRef.current = null;
      }
      // Close AudioContext to release resources
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
          console.error('SoundMeter: Failed to close AudioContext');
        });
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      dataArrayRef.current = null;
    };
  }, [externalStream]);

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: alpha(theme.palette.primary.main, 0.5),
        borderRadius: '6px',
        padding: '9px',
        overflow: 'hidden',
        height: '32px',
      })}
    >
      {levels.map((level, i) => {
        const isIdle = level < 8;

        return (
          <Box
            key={i}
            sx={{
              width: '2px',
              margin: '0 2px',
              height: isIdle ? '2px' : `${(level / 255) * 14}px`,
              background: '#E6F3FF',
              borderRadius: isIdle ? '50%' : '0.5px',
              transition: 'height 0.2s ease-out, border-radius 0.2s ease-out',
              maxHeight: '14px',
            }}
          />
        );
      })}
    </Box>
  );
};
